import { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, BookMarked, MessageCircle, Trophy, HandHeart } from "lucide-react";

/**
 * Jixxeo Helping Hands — Phase A shell.
 * Green money-saving palette, mobile-first, sticky bottom nav.
 * Dark-mode default so it feels like a fintech app.
 */
export default function HandsLayout() {
  const nav = useNavigate();

  useEffect(() => {
    document.body.classList.add("hands-scope");
    return () => document.body.classList.remove("hands-scope");
  }, []);

  return (
    <div data-testid="hands-shell" className="min-h-screen bg-[#0A1512] text-[#E7F5EE] pb-24 relative">
      <HandsScopedStyles />
      <header className="sticky top-0 z-40 bg-[#0A1512]/95 backdrop-blur border-b border-[#1D3A2E]">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/helpinghands" data-testid="hands-logo" className="flex items-center gap-2">
            <HandsLogo />
            <div className="leading-tight">
              <div className="font-black text-[16px] tracking-tight text-white">Jixxeo <span className="text-[#22C55E]">Helping Hands</span></div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">personal money coach</div>
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pt-4">
        <Outlet />
      </main>

      <nav data-testid="hands-bottom-nav"
        className="fixed bottom-0 inset-x-0 z-40 bg-[#0A1512]/95 backdrop-blur border-t border-[#1D3A2E]">
        <div className="max-w-4xl mx-auto grid grid-cols-5">
          <BottomTab to="/helpinghands" end icon={<LayoutDashboard className="w-5 h-5" />} label="Home" testId="hands-nav-home" />
          <BottomTab to="/helpinghands/ask" icon={<Sparkles className="w-5 h-5" />} label="Ask A.I" testId="hands-nav-ask" />
          <BottomTab to="/helpinghands/plans" icon={<BookMarked className="w-5 h-5" />} label="Plans" testId="hands-nav-plans" />
          <BottomTab to="/helpinghands/community" icon={<MessageCircle className="w-5 h-5" />} label="Community" testId="hands-nav-community" />
          <BottomTab to="/helpinghands/badges" icon={<Trophy className="w-5 h-5" />} label="Badges" testId="hands-nav-badges" />
        </div>
      </nav>
    </div>
  );
}

function BottomTab({ to, end, icon, label, testId }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) => "flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors " + (isActive ? "text-[#22C55E]" : "text-white/50 hover:text-white/80")}>
      {icon}
      <span className="text-[10px] font-semibold tracking-wide">{label}</span>
    </NavLink>
  );
}

/** The two-brown-helping-hands logo (SVG, small, no external asset needed). */
export function HandsLogo({ size = 36 }) {
  return (
    <div className="rounded-xl bg-gradient-to-br from-[#B08052] to-[#6E4B2F] flex items-center justify-center shadow-md" style={{ width: size, height: size }}>
      <svg viewBox="0 0 40 40" width={size * 0.7} height={size * 0.7} fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Left hand */}
        <path d="M6 22 C6 18, 8 16, 11 16 L14 16 L14 12 C14 10, 15.5 9, 17 9 C18.5 9, 20 10, 20 12 L20 22 L20 26 C20 30, 17 32, 14 32 L11 32 C8 32, 6 30, 6 26 Z"
          fill="#E7B98D" stroke="#5C3A20" strokeWidth="1.2" strokeLinejoin="round" />
        {/* Right hand mirrored */}
        <path d="M34 22 C34 18, 32 16, 29 16 L26 16 L26 12 C26 10, 24.5 9, 23 9 C21.5 9, 20 10, 20 12 L20 22 L20 26 C20 30, 23 32, 26 32 L29 32 C32 32, 34 30, 34 26 Z"
          fill="#C69574" stroke="#5C3A20" strokeWidth="1.2" strokeLinejoin="round" />
        {/* Small heart between */}
        <path d="M20 20 C18.5 18.5, 17 19, 17 21 C17 22.5, 19 24, 20 25 C21 24, 23 22.5, 23 21 C23 19, 21.5 18.5, 20 20 Z"
          fill="#22C55E" />
      </svg>
    </div>
  );
}

function HandsScopedStyles() {
  return (
    <style>{`
      body.hands-scope { background:#0A1512 !important; color:#E7F5EE !important; }
      body.hands-scope .noise-overlay, body.hands-scope .grid-backdrop { display:none !important; }
    `}</style>
  );
}
